import { useAtom } from 'jotai';

import { isSalesModeAtom } from '@rocco/states/apAdminUiContext';
import { useEffect, useRef } from 'react';
import { useLocation } from '@remix-run/react';
import { useToaster } from '../context/ToasterContext';

// Create a more flexible type that accepts serialized user data
type UserWithLeadershipTeam = { IsLeadershipTeam?: boolean } | null;

export const useSalesMode = (user: UserWithLeadershipTeam) => {
    const [isSalesMode, setIsSalesMode] = useAtom(isSalesModeAtom);
    const location = useLocation();
    const canSeeSalesData = user?.IsLeadershipTeam;

    const { toast } = useToaster();

    const timeoutId = useRef<NodeJS.Timeout | null>(null);
    // Auto-disable sales mode after 10 seconds of no page navigation
    useEffect(() => {
        if (!isSalesMode) return;

        if (timeoutId.current) {
            clearTimeout(timeoutId.current);
        }

        timeoutId.current = setTimeout(
            () => {
                setIsSalesMode(false);
                toast('Exited Sales Admin due to inactivity', {
                    type: 'notification',
                });
            },
            1000 * 60 * 5
        ); // 5 minutes

        return () => {
            if (timeoutId.current) {
                clearTimeout(timeoutId.current);
            }
        };
    }, [
        isSalesMode,
        setIsSalesMode,
        location.pathname,
        location.search,
        toast,
    ]);

    // For serialized user data, we can't use isUserLoggedIn, so check if user exists and has the required property
    if (!user || !canSeeSalesData) {
        return {
            canSeeSalesData: false,
            isSalesMode: false,
            toggleSalesMode: () => {},
        };
    }

    return {
        canSeeSalesData,
        isSalesMode,
        toggleSalesMode: () => {
            setIsSalesMode(!isSalesMode);
        },
    };
};
